import React from "react";
import {
  Box,
  Typography,
  Fade,
  Slide,
  Paper,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  ErrorOutline,
  WifiOff,
  Refresh,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setWebsocketDisconnected, setWebsocketReconnect } from "../../Redux/Reducers/uiSlice";
import { selectWebsocketReconnect } from "../../Redux/Reducers/selectors";
import { useWebSocketStatus } from "../../hooks/useWebSocket";

export const WebsocketDisconnectionNotification = ({ message = "Connection Lost", onReconnect }) => {
  const dispatch = useDispatch();
  const isReconnectMode = useSelector(selectWebsocketReconnect);
  const { isReconnecting, reconnectAttempts, maxReconnectAttempts } = useWebSocketStatus();


  const handleReconnect = async () => {
    if (onReconnect) {
      onReconnect();
    }
  };

  return (
    <Fade in={true} timeout={300}>
      <Slide direction="up" in={true} timeout={300}>
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            zoom: 0.75,
            right: 24,
            zIndex: 9999,
            maxWidth: 400,
            minWidth: 320,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: "#d32f2f", // Red background
              border: "1px solid rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
            }}
          >
            {/* Header with icon and close button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 20px 12px 20px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    color: "white",
                  }}
                >
                  <WifiOff sx={{ fontSize: "18px" }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "white",
                    lineHeight: 1.2,
                  }}
                >
                  {isReconnectMode ? "Please Wait" : "Connection Lost"}
                </Typography>
              </Box>
            </Box>

            {/* Content */}
            <Box sx={{ padding: "16px 20px 20px 20px" }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                    borderRadius: "6px",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "white",
                    flexShrink: 0,
                    marginTop: "2px",
                  }}
                >
                  {isReconnecting ? (
                    <CircularProgress size={14} sx={{ color: "white" }} />
                  ) : (
                    <ErrorOutline sx={{ fontSize: "14px" }} />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: 1.3,
                      mb: 0.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "white",
                    }}
                  >
                   {isReconnectMode ? "Syncing Data" : "WebSocket connection lost"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "11px",
                      opacity: 0.8,
                      lineHeight: 1.2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "white",
                      mb: isReconnecting ? 1 : 1.5,
                    }}
                  >
                    {isReconnecting
                      ? `Attempting to reconnect to server... (${reconnectAttempts}/${maxReconnectAttempts})`
                      : isReconnectMode
                        ? "Fetching Seed Data..."
                        : "Click the button below to try reconnecting"}
                  </Typography>
                  {!isReconnectMode && (
                    <Button
                    variant="contained"
                    startIcon={ <Refresh />}
                    onClick={handleReconnect}
                    disabled={isReconnecting}
                    sx={{
                      backgroundColor: isReconnecting ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.2)",
                      color: "white",
                      '&:hover': {
                        backgroundColor: isReconnecting ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.3)",
                      },
                      fontSize: "12px",
                      padding: "6px 16px",
                      minWidth: "auto",
                      mt: 2
                    }}
                  >
                    {isReconnecting ? "Reconnecting..." : "Reconnect"}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Progress bar */}
            <Box
              sx={{
                height: 3,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: "100%",
                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                  animation: "pulse 2s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%": {
                      opacity: 0.3,
                    },
                    "50%": {
                      opacity: 0.7,
                    },
                    "100%": {
                      opacity: 0.3,
                    },
                  },
                }}
              />
            </Box>
          </Paper>
        </Box>
      </Slide>
    </Fade>
  );
};
